import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { TemplateDraft, TemplateDraftStatus, FinalizeTemplateDraftResult, FinalizeTemplateDraftRequest } from './template-draft.model';
import { Template } from '../template/template.model';
import { TemplatePreview } from '../template-preview/template-preview.model';

@Injectable({ providedIn: 'root' })
export class TemplateDraftService {
    private readonly baseUrl = `${environment.rootUrl}/template-draft`;

    constructor(private http: HttpClient) {}

    /**
     * Get a single template draft by ID.
     */
    get(draftId: number): Observable<TemplateDraft> {
        return this.http.get<TemplateDraft>(`${this.baseUrl}/${draftId}`);
    }

    /**
     * Get all template drafts for a category, optionally filtered by status.
     */
    getByCategory(categoryId: number, status?: TemplateDraftStatus): Observable<TemplateDraft[]> {
        let url = `${this.baseUrl}/category/${categoryId}`;
        if (status !== undefined) {
            url += `?status=${status}`;
        }
        return this.http.get<TemplateDraft[]>(url);
    }

    /**
     * Create a new template draft.
     */
    create(draft: Partial<TemplateDraft>): Observable<TemplateDraft> {
        return this.http.post<TemplateDraft>(this.baseUrl, draft);
    }

    /**
     * Update an existing template draft.
     */
    update(draft: TemplateDraft): Observable<TemplateDraft> {
        return this.http.patch<TemplateDraft>(`${this.baseUrl}/${draft.id}`, draft);
    }

    /**
     * Delete a template draft and all linked templates.
     */
    remove(draftId: number): Observable<void> {
        return this.http.delete<void>(`${this.baseUrl}/${draftId}`);
    }

    /**
     * Add templates to a draft for the given products.
     */
    addTemplates(draftId: number, productIds: number[]): Observable<Template[]> {
        return this.http.post<Template[]>(`${this.baseUrl}/${draftId}/templates`, productIds);
    }

    /**
     * Finalize a draft: sets template status to Complete, moves templates to target category, and deletes the draft.
     * Returns the root and target category IDs for navigation.
     */
    finalize(draftId: number, request?: FinalizeTemplateDraftRequest): Observable<FinalizeTemplateDraftResult> {
        return this.http.post<FinalizeTemplateDraftResult>(`${this.baseUrl}/${draftId}/finalize`, request || {});
    }

    /**
     * Start editing a completed draft: sets draft status to InProgress and template status to Draft.
     */
    startEdit(draftId: number): Observable<TemplateDraft> {
        return this.http.post<TemplateDraft>(`${this.baseUrl}/${draftId}/start-edit`, {});
    }

    /**
     * Persist preview images as template previews for all templates in a draft.
     */
    generatePreviews(draftId: number, previewImages: { productId: number; faceIndex: number; base64Image: string }[]): Observable<void> {
        return this.http.post<void>(`${this.baseUrl}/${draftId}/generate-previews`, previewImages);
    }

    /**
     * Edit an existing complete template by creating a new draft for it.
     */
    editTemplate(templateId: number): Observable<TemplateDraft> {
        return this.http.post<TemplateDraft>(`${this.baseUrl}/edit/${templateId}`, {});
    }

    /**
     * Edit multiple existing complete templates by creating a new draft for them.
     */
    editTemplates(templateIds: number[]): Observable<TemplateDraft> {
        return this.http.post<TemplateDraft>(`${this.baseUrl}/edit-multiple`, templateIds);
    }

    /**
     * Create a OneTime draft for ordering existing templates.
     * Resolves templates from direct IDs, bundles, and categories.
     */
    createOrder(data: { templateIds: number[]; bundleIds: number[]; categoryIds: number[] }): Observable<TemplateDraft> {
        return this.http.post<TemplateDraft>(`${this.baseUrl}/create-order`, data);
    }

    /**
     * Edit templates resolved from direct IDs, bundles, and categories.
     * Creates a draft for editing with the templates' original type.
     */
    editSelection(data: { templateIds: number[]; bundleIds: number[]; categoryIds: number[] }): Observable<TemplateDraft> {
        return this.http.post<TemplateDraft>(`${this.baseUrl}/edit-selection`, data);
    }

    /**
     * Get existing template previews for all templates in a draft.
     */
    getPreviews(draftId: number): Observable<TemplatePreview[]> {
        return this.http.get<TemplatePreview[]>(`${this.baseUrl}/${draftId}/previews`);
    }
}
